import type { IndustryItem, SiteData, Service } from '../../shared/types/site.js';
import { defaultSiteData } from '../data/defaults.js';
import { CONTACT_IMAGE, DEFAULT_INDUSTRY_IMAGES, getServiceImageUrl, iconSvg } from '../constants.js';
import { getSector, type Sector } from '../sector.js';
import { $, escapeHtml } from '../utils/index.js';

type SectorCopy = {
  eyebrow: string;
  intro: string;
  primaryCta: string;
  secondaryCta: string;
  servicesEyebrow: string;
  servicesTitle: string;
  servicesIntro: string;
  processEyebrow: string;
  processTitle: string;
  processIntro: string;
  processSteps: Array<{ title: string; body: string }>;
  ctaEyebrow: string;
  ctaTitle: string;
  ctaBody: string;
};

const HOME_PROOF_QUOTES = [
  {
    quote: 'Senior clarity, calm delivery, and the kind of technical judgment you want when the product actually matters.',
    author: 'VP Engineering',
    role: 'SaaS Platform',
  },
  {
    quote: 'The team moved fast without turning the system into future debt. That balance is hard to find.',
    author: 'Product Director',
    role: 'Financial Services',
  },
  {
    quote: 'Communication stayed precise, decisions were grounded, and the final handover felt production-ready from day one.',
    author: 'Head of Digital',
    role: 'Public Sector',
  },
  {
    quote: 'We needed strong execution under pressure. ARKRON brought structure, reliability, and real ownership.',
    author: 'CTO',
    role: 'Growth Company',
  },
] as const;

const HERO_HEADLINES = [
  'We build ideas for you',
  'We build products for you',
  'We craft platforms for institutions',
  'We engineer systems for governments',
  'We design products for businesses',
  'We test platforms for institutions',
  'We launch systems for governments',
] as const;

const LABS_HERO_HEADLINES = [
  'We build ideas',
  'We build products',
  'We craft platforms',
  'We test concepts',
  'We launch games',
  'We shape tools',
] as const;

const HOME_COPY: Record<Sector, SectorCopy> = {
  tech: {
    eyebrow: 'Arkron Tech',
    intro:
      'We design and deliver digital products built to exceed expectations, with our name on the line with every release.',
    primaryCta: 'Contact Us',
    secondaryCta: 'See what we do',
    servicesEyebrow: 'What we do',
    servicesTitle: 'The work we do for clients.',
    servicesIntro:
      'Web, mobile, AI, and product delivery.',
    processEyebrow: 'How we work',
    processTitle: 'A steady way of shipping.',
    processIntro:
      'Clear decisions, steady delivery, strong follow-through.',
    processSteps: [
      {
        title: 'Understand the goal',
        body: 'We start by getting clear on the product, the users, and the real business need.',
      },
      {
        title: 'Shape the right solution',
        body: 'We define the system, the screens, and the scope before things become expensive to change.',
      },
      {
        title: 'Build in small steps',
        body: 'We keep progress visible, quality high, and communication direct while the product takes shape.',
      },
      {
        title: 'Stay after launch',
        body: 'We help improve speed, stability, and the next version of the product once it is live.',
      },
    ],
    ctaEyebrow: 'Start a conversation',
    ctaTitle: 'Tell us what you are building.',
    ctaBody:
      'We can help shape the next step.',
  },
  labs: {
    eyebrow: 'Arkron Labs',
    intro:
      'Arkron Labs is where we build our own products, test ideas, and back work we believe matters.',
    primaryCta: 'See the walkthrough',
    secondaryCta: 'Contact Us',
    servicesEyebrow: 'What Labs is focused on',
    servicesTitle: 'A small set of things we want to move forward.',
    servicesIntro:
      'What we are building and where it stands.',
    processEyebrow: 'What shapes our products',
    processTitle: 'Products built around real-world needs.',
    processIntro:
      'We focus on software that removes friction, sharpens operations, and creates room for better business decisions.',
    processSteps: [
      {
        title: 'Solve and simplify real-world problems',
        body: 'We build products that reduce day-to-day friction and turn messy, manual work into something clearer and easier to run.',
      },
      {
        title: 'Open economic interaction',
        body: 'We want software that makes global trade, coordination, and cross-border business activity easier to access and manage.',
      },
      {
        title: 'All-in-one business management',
        body: 'We care about products that simplify operations, connect moving parts, and give teams one place to manage the work that matters.',
      },
      {
        title: 'Niche products for niche markets',
        body: 'Some of the best opportunities are specific and underserved, so we are willing to build focused products for markets others ignore.',
      },
    ],
    ctaEyebrow: 'Keep up with it',
    ctaTitle: 'Want to follow or support what we are building?',
    ctaBody:
      'Reach out if you want to follow or support it.',
  },
};

const LABS_OBJECTIVES = [
  {
    title: 'Building a mobile AI solution for agriculture',
    body: 'Native workflows for field teams, crop insight, and everyday operational decisions.',
    status: 'Currently',
    active: true,
  },
  {
    title: 'Soon to be revealed',
    body: 'An essential product for priorities, follow-ups, and day-to-day execution.',
    status: 'Currently',
    active: true,
  },
  {
    title: 'Designing an internal product intelligence layer',
    body: 'Shared tooling for research, validation, and sharper product decisions.',
    status: 'Planned',
    active: false,
  },
  {
    title: 'Making a game',
    body: 'Gameplay direction, world-building, and production planning for the first release.',
    status: 'Planned',
    active: false,
  },
] as const;

function renderHeroTitle(headlines: readonly string[] = HERO_HEADLINES): string {
  const initialHeadline = headlines[0] ?? 'We build ideas';

  return `
    <h1 class="hero-title hero-title-typing-inline" aria-label="${escapeHtml(initialHeadline)}">
      <span class="hero-typing-line">
        <span class="hero-typing-prefix">We</span>
        <span class="hero-typing-text"></span>
      </span>
      <span class="hero-typing-source" hidden aria-hidden="true">
        ${headlines.map((line) => `<span>${escapeHtml(line)}</span>`).join('')}
      </span>
    </h1>
  `;
}

let stopHomeScrollEffects: (() => void) | null = null;
let stopHomeMarquees: (() => void) | null = null;
let stopHeroTitleTyping: (() => void) | null = null;

function clearHomeScrollEffects(): void {
  if (stopHomeScrollEffects) {
    stopHomeScrollEffects();
    stopHomeScrollEffects = null;
  }
  document.documentElement.style.removeProperty('--hero-scroll');
}

function clearHomeMarquees(): void {
  if (stopHomeMarquees) {
    stopHomeMarquees();
    stopHomeMarquees = null;
  }
}

function clearHeroTitleTyping(): void {
  if (stopHeroTitleTyping) {
    stopHeroTitleTyping();
    stopHeroTitleTyping = null;
  }
}

function initHomeScrollEffects(root: HTMLElement): void {
  clearHomeScrollEffects();

  const controller = new AbortController();
  const { signal } = controller;
  const hero = root.querySelector<HTMLElement>('.hero');
  if (!hero) {
    stopHomeScrollEffects = () => controller.abort();
    return;
  }

  const update = () => {
    const rect = hero.getBoundingClientRect();
    const viewportHeight = Math.max(window.innerHeight, 1);
    const travel = Math.max(rect.height - viewportHeight * 0.3, viewportHeight * 0.45, 1);
    const progressed = Math.min(Math.max(-rect.top / travel, 0), 1);
    document.documentElement.style.setProperty('--hero-scroll', progressed.toFixed(3));
  };

  update();
  window.addEventListener('scroll', update, { passive: true, signal });
  window.addEventListener('resize', update, { signal });
  window.addEventListener('pageshow', update, { signal });

  stopHomeScrollEffects = () => {
    controller.abort();
    document.documentElement.style.removeProperty('--hero-scroll');
  };
}

function restartHeroAnimation(root: HTMLElement): void {
  const animated = root.ownerDocument.querySelectorAll<HTMLElement>('.global-bg .hero-bg-animated');
  animated.forEach((element) => {
    element.style.animation = 'none';
  });
  void root.offsetHeight;
  animated.forEach((element) => {
    element.style.animation = '';
  });
}

function initHeroTitleTyping(root: HTMLElement): void {
  clearHeroTitleTyping();

  const titles = Array.from(root.querySelectorAll<HTMLElement>('.hero-title-typing-inline'));
  if (titles.length === 0) return;

  const controller = new AbortController();
  const { signal } = controller;
  const timers = new Set<number>();
  let resizeFrame = 0;

  const schedule = (task: () => void, delay: number) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      task();
    }, delay);
    timers.add(timer);
  };

  type HeroTypingState = {
    title: HTMLElement;
    text: HTMLElement;
    phrases: string[];
    phraseIndex: number;
    charIndex: number;
    deleting: boolean;
    lastMeasuredWidth: number;
  };

  const states = titles
    .map((title) => {
      const text = title.querySelector<HTMLElement>('.hero-typing-text');
      if (!text) return null;

      let phrases = Array.from(title.querySelectorAll<HTMLElement>('.hero-typing-source span'))
        .map((node) => node.textContent?.trim() ?? '')
        .filter((phrase) => phrase.length > 0);

      if (phrases.length === 0) {
        phrases = [title.getAttribute('aria-label') || 'We build ideas'];
      }

      const normalizedPhrases = phrases
        .map((phrase) => phrase.replace(/^We\s+/i, '').trim())
        .filter((phrase) => phrase.length > 0);

      if (normalizedPhrases.length === 0) {
        normalizedPhrases.push('build ideas');
      }

      text.textContent = ' ';

      return {
        title,
        text,
        phrases: normalizedPhrases,
        phraseIndex: 0,
        charIndex: 0,
        deleting: false,
        lastMeasuredWidth: 0,
      } satisfies HeroTypingState;
    })
    .filter((state): state is HeroTypingState => Boolean(state));

  if (states.length === 0) {
    stopHeroTitleTyping = () => controller.abort();
    return;
  }

  const measureState = (state: HeroTypingState, force = false) => {
    const currentWidth = Math.round(state.title.getBoundingClientRect().width);
    if (!force && currentWidth > 0 && Math.abs(currentWidth - state.lastMeasuredWidth) < 2) {
      return;
    }

    const measurer = document.createElement('span');
    measurer.className = 'hero-typing-measure';
    if (window.innerWidth >= 900) {
      measurer.style.whiteSpace = 'nowrap';
    }
    state.title.appendChild(measurer);

    let maxHeight = 0;
    state.phrases.forEach((phrase) => {
      measurer.textContent = `We ${phrase}`;
      maxHeight = Math.max(maxHeight, measurer.getBoundingClientRect().height);
    });

    measurer.remove();

    if (maxHeight > 0) {
      state.title.style.minHeight = `${Math.ceil(maxHeight)}px`;
    }

    if (currentWidth > 0) {
      state.lastMeasuredWidth = currentWidth;
    }
  };

  const typeNext = (state: HeroTypingState) => {
    if (document.hidden) {
      schedule(() => typeNext(state), 220);
      return;
    }

    const phrase = state.phrases[state.phraseIndex] ?? '';
    if (!state.deleting) {
      state.charIndex = Math.min(state.charIndex + 1, phrase.length);
      state.text.textContent = ` ${phrase.slice(0, state.charIndex)}`;
      if (state.charIndex >= phrase.length) {
        state.deleting = true;
        schedule(() => typeNext(state), 1400);
        return;
      }
      const currentCharacter = phrase[state.charIndex - 1] ?? '';
      schedule(() => typeNext(state), currentCharacter === ' ' ? 72 : 56);
      return;
    }

    state.charIndex = Math.max(state.charIndex - 1, 0);
    state.text.textContent = state.charIndex > 0 ? ` ${phrase.slice(0, state.charIndex)}` : ' ';
    if (state.charIndex === 0) {
      state.deleting = false;
      state.phraseIndex = (state.phraseIndex + 1) % state.phrases.length;
      schedule(() => typeNext(state), 260);
      return;
    }
    schedule(() => typeNext(state), 42);
  };

  const measureAll = (force = false) => {
    states.forEach((state) => measureState(state, force));
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      measureAll(true);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        states.forEach((state) => {
          state.text.textContent = ` ${state.phrases[0] ?? 'build ideas'}`;
        });
        return;
      }
      states.forEach((state, index) => {
        schedule(() => typeNext(state), 180 + index * 120);
      });
    });
  });

  const onResize = () => {
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      measureAll();
    });
  };

  const onPageShow = () => measureAll(true);

  window.addEventListener('resize', onResize, { signal });
  window.addEventListener('pageshow', onPageShow, { signal });

  stopHeroTitleTyping = () => {
    controller.abort();
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = 0;
    }
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
    states.forEach((state) => {
      state.title.style.removeProperty('min-height');
    });
  };
}

function renderIndustryCard(item: IndustryItem): string {
  const imageUrl = DEFAULT_INDUSTRY_IMAGES[item.slug] ?? '';
  return `
    <article class="industry-marquee-card">
      <div class="industry-marquee-media" aria-hidden="true">
        ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" />` : ''}
      </div>
      <div class="industry-marquee-body">
        <h3>${escapeHtml(item.title)}</h3>
        <p>${escapeHtml(item.description)}</p>
      </div>
    </article>
  `;
}

function renderProofCard(item: (typeof HOME_PROOF_QUOTES)[number]): string {
  return `
    <article class="review-marquee-card">
      <p class="review-marquee-quote">“${escapeHtml(item.quote)}”</p>
      <p class="review-marquee-meta">${escapeHtml(item.author)} · ${escapeHtml(item.role)}</p>
    </article>
  `;
}

function renderMarqueeSets<T>(items: readonly T[], renderItem: (item: T) => string): string {
  return Array.from({ length: 4 }, () => `
    <div class="loop-marquee-set">
      ${items.map((item) => renderItem(item)).join('')}
    </div>
  `).join('');
}

function initHomeMarquees(root: HTMLElement): void {
  clearHomeMarquees();

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const marquees = Array.from(root.querySelectorAll<HTMLElement>('[data-loop-marquee]'));
  if (marquees.length === 0) return;

  const cleanups: Array<() => void> = [];

  marquees.forEach((marquee) => {
    const track = marquee.querySelector<HTMLElement>('.loop-marquee-track');
    if (!track) return;

    let frameId = 0;
    let lastTime = performance.now();
    let offset = 0;
    let paused = false;

    const getGap = () => {
      const styles = getComputedStyle(track);
      const rawGap = styles.columnGap || styles.gap || '0';
      const parsed = Number.parseFloat(rawGap);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const step = (time: number) => {
      const delta = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;

      if (!paused) {
        const speed = Number.parseFloat(marquee.dataset.speed ?? '30') || 30;
        offset -= speed * delta;

        const firstSet = track.firstElementChild as HTMLElement | null;
        if (firstSet) {
          const recycleWidth = firstSet.offsetWidth + getGap();
          if (recycleWidth > 0 && -offset >= recycleWidth) {
            offset += recycleWidth;
            track.appendChild(firstSet);
          }
        }

        track.style.transform = `translate3d(${offset}px, 0, 0)`;
      }

      frameId = window.requestAnimationFrame(step);
    };

    const pause = () => {
      paused = true;
    };

    const resume = () => {
      paused = false;
      lastTime = performance.now();
    };

    marquee.addEventListener('mouseenter', pause);
    marquee.addEventListener('mouseleave', resume);

    frameId = window.requestAnimationFrame(step);

    cleanups.push(() => {
      window.cancelAnimationFrame(frameId);
      marquee.removeEventListener('mouseenter', pause);
      marquee.removeEventListener('mouseleave', resume);
      track.style.removeProperty('transform');
    });
  });

  stopHomeMarquees = () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}

function renderServiceCard(service: Service, index: number): string {
  const imageUrl = getServiceImageUrl(service);
  return `
    <article class="service-card animate-on-scroll" style="animation-delay: ${(index % 2) * 0.05}s">
      <div class="service-card-visual" aria-hidden="true">
        ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" />` : ''}
        <div class="service-card-icon">${iconSvg[service.icon] ?? iconSvg.web}</div>
      </div>
      <div class="service-card-body">
        <h3>${escapeHtml(service.title)}</h3>
        <p>${escapeHtml(service.description)}</p>
        <a href="/services/${encodeURIComponent(service.slug)}" class="text-link service-card-cta">Open service</a>
      </div>
    </article>
  `;
}

function renderTechHome(siteData: SiteData): string {
  const services = (siteData.services ?? defaultSiteData.services ?? []).slice(0, 8);
  const industries = (siteData.industries?.items ?? defaultSiteData.industries?.items ?? []).slice(0, 6);
  const copy = HOME_COPY.tech;
  const contact = siteData.contact ?? defaultSiteData.contact!;
  return `
    <section class="hero section">
      <div class="container hero-home-layout">
        <div class="hero-copy hero-copy-home hero-copy-centered animate-on-scroll">
          ${renderHeroTitle()}
          <p class="hero-body hero-body-wide">${copy.intro}</p>
        </div>
      </div>
    </section>
    <section class="section" id="home-services" data-route-anchor="services">
      <div class="container section-head animate-on-scroll">
        <h2 class="section-title section-title-medium">What we do for clients</h2>
        <p class="section-intro">${copy.servicesIntro}</p>
      </div>
      <div class="container services-showcase">
        ${services.map((service, index) => renderServiceCard(service, index)).join('')}
      </div>
    </section>
    <section class="section" id="home-areas">
      <div class="container section-head animate-on-scroll">
        <h2 class="section-title section-title-medium">Areas we know well</h2>
      </div>
      <div class="container">
        <div class="loop-marquee animate-on-scroll" data-loop-marquee data-speed="28">
          <div class="loop-marquee-viewport">
            <div class="loop-marquee-track loop-marquee-track-industries">
              ${renderMarqueeSets(industries, renderIndustryCard)}
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="section" id="home-philosophy" data-route-anchor="philosophy">
      <div class="container section-head animate-on-scroll">
        <h2 class="section-title section-title-medium">Trusted by teams that value technical depth.</h2>
      </div>
      <div class="container">
        <div class="loop-marquee animate-on-scroll" data-loop-marquee data-speed="22">
          <div class="loop-marquee-viewport">
            <div class="loop-marquee-track loop-marquee-track-reviews">
              ${renderMarqueeSets(HOME_PROOF_QUOTES, renderProofCard)}
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="section" id="home-delivery">
      <div class="container section-head animate-on-scroll">
        <p class="section-kicker">Delivery standards</p>
        <h2 class="section-title section-title-medium">Built for high-stakes products.</h2>
        <p class="section-intro">Clear scope, strong architecture, reliable release.</p>
      </div>
      <div class="container delivery-showcase">
        <article class="delivery-card animate-on-scroll">
          <div class="delivery-card-visual delivery-card-visual-terminal" aria-hidden="true">
            <div class="delivery-window-dots"><span></span><span></span><span></span></div>
            <div class="delivery-console">
              <p>&gt; scope approved · milestones locked</p>
              <p>&gt; build progressing · release train stable</p>
              <p>&gt; qa passed · launch readiness confirmed</p>
            </div>
          </div>
          <div class="delivery-card-body">
            <h3>Product delivery</h3>
            <p>We move from discovery to release with clear decisions, visible progress, and no process theatre.</p>
            <div class="delivery-chip-row">
              <span class="tech-chip">Architecture</span>
              <span class="tech-chip">Implementation</span>
              <span class="tech-chip">QA + Launch</span>
            </div>
          </div>
        </article>
        <article class="delivery-card animate-on-scroll" style="animation-delay: 0.05s">
          <div class="delivery-card-visual delivery-card-visual-metrics" aria-hidden="true">
            <div class="delivery-window-dots"><span></span><span></span><span></span></div>
            <div class="delivery-metric-grid">
              <div class="delivery-metric"><strong>99.99%</strong><span>Uptime</span></div>
              <div class="delivery-metric"><strong>142ms</strong><span>P95 API</span></div>
              <div class="delivery-metric"><strong>0</strong><span>Critical alerts</span></div>
            </div>
          </div>
          <div class="delivery-card-body">
            <h3>Systems reliability</h3>
            <p>We design for observability, rollback safety, performance, and calm operations once the product is live.</p>
            <div class="delivery-chip-row">
              <span class="tech-chip">Monitoring</span>
              <span class="tech-chip">Incident readiness</span>
              <span class="tech-chip">Performance</span>
            </div>
          </div>
        </article>
      </div>
    </section>
    <section class="section">
      <div class="container collaborate-panel animate-on-scroll">
        <div class="collaborate-copy">
          <h2 class="section-title light section-title-medium">Let&apos;s collaborate</h2>
          <p class="section-intro light">${escapeHtml(contact.subtitle || copy.ctaBody)}</p>
          <div class="cta-panel-actions">
            <a href="/contact-us" class="btn-primary">${escapeHtml(contact.cta || copy.primaryCta)}</a>
          </div>
        </div>
        <div class="collaborate-visual" aria-hidden="true">
          <img src="${CONTACT_IMAGE}" alt="" loading="lazy" />
        </div>
      </div>
    </section>
  `;
}

function renderLabsHome(siteData: SiteData): string {
  const copy = HOME_COPY.labs;
  return `
    <section class="hero section">
      <div class="container hero-home-layout">
        <div class="hero-copy hero-copy-home hero-copy-centered animate-on-scroll">
          ${renderHeroTitle(LABS_HERO_HEADLINES)}
          <p class="hero-body hero-body-wide">${copy.intro}</p>
        </div>
      </div>
    </section>
    <section class="section" id="home-walkthrough" data-route-anchor="walkthrough">
      <div class="container section-head animate-on-scroll">
        <h2 class="section-title section-title-medium">${copy.servicesTitle}</h2>
        <p class="section-intro">${copy.servicesIntro}</p>
      </div>
      <div class="container walkthrough-wrap">
        <div class="walkthrough-track" aria-hidden="true">
          ${LABS_OBJECTIVES
            .map(
              (objective, index) => `
                <span class="walkthrough-track-item${objective.active ? ' is-active' : ''}${index === LABS_OBJECTIVES.length - 1 ? ' is-last' : ''}">
                  <span class="walkthrough-track-dot"></span>
                  <span class="walkthrough-track-segment"></span>
                </span>
              `
            )
            .join('')}
        </div>
        <div class="walkthrough-grid">
          ${LABS_OBJECTIVES
            .map(
              (objective, index) => `
                <article class="walkthrough-card animate-on-scroll${objective.active ? ' is-done' : ''}" style="animation-delay: ${index * 0.04}s">
                  <div class="walkthrough-meta">
                    <span class="walkthrough-status">${escapeHtml(objective.status)}</span>
                  </div>
                  <h3>${escapeHtml(objective.title)}</h3>
                  <p>${escapeHtml(objective.body)}</p>
                </article>
              `
            )
            .join('')}
        </div>
      </div>
    </section>
    <section class="section" id="home-philosophy" data-route-anchor="philosophy">
      <div class="container section-head animate-on-scroll">
        <h2 class="section-title section-title-medium">${copy.processTitle}</h2>
        <p class="section-intro">${copy.processIntro}</p>
      </div>
      <div class="container process-grid">
        ${copy.processSteps
          .map(
            (step, index) => `
              <article class="process-card animate-on-scroll" style="animation-delay: ${(index % 2) * 0.05}s">
                <span class="process-number">0${index + 1}</span>
                <h3>${escapeHtml(step.title)}</h3>
                <p>${escapeHtml(step.body)}</p>
              </article>
            `
          )
          .join('')}
      </div>
    </section>
    <section class="section">
      <div class="container cta-panel animate-on-scroll cta-panel-compact">
        <div>
          <h2 class="section-title light section-title-medium">${copy.ctaTitle}</h2>
          <p class="section-intro light">${copy.ctaBody}</p>
        </div>
        <div class="cta-panel-actions">
          <a href="/contact-us" class="btn-primary">Contact Us</a>
        </div>
      </div>
    </section>
  `;
}

export function onHomeActivated(): void {
  const home = document.getElementById('page-home');
  if (!(home instanceof HTMLElement) || home.hidden) return;
  restartHeroAnimation(home);
  initHomeScrollEffects(home);
}

export function renderHome(siteData: SiteData): void {
  const el = $('#page-home');
  if (!el) return;

  const sector = getSector();
  clearHomeMarquees();
  clearHeroTitleTyping();
  el.innerHTML = sector === 'labs' ? renderLabsHome(siteData) : renderTechHome(siteData);
  initHeroTitleTyping(el);
  initHomeScrollEffects(el);
  if (sector === 'tech') {
    initHomeMarquees(el);
  }
}
